import type { EntityViewLevel, LayerModel, WorkScope } from '../core/types';
import { emptyWorkScope, normalizeWorkScope } from './scope';

/** A navigation target: which level to "Ver por" inside which hierarchical WorkScope. */
export interface NavigationTarget {
  readonly level: EntityViewLevel;
  readonly scope: WorkScope;
  /** Entity to keep selected after navigating (e.g. returning to the parent view). */
  readonly focus?: { readonly kind: EntityViewLevel; readonly id: string };
}

export interface Crumb extends NavigationTarget {
  readonly label: string;
  readonly current: boolean;
}

const childLevel: Readonly<Partial<Record<EntityViewLevel, EntityViewLevel>>> = { finca: 'suerte', suerte: 'tablon' };

/** Drill-down never mixes with classification: it only changes level + scope. */
export function drillDownTarget(model: LayerModel, kind: EntityViewLevel, id: string): NavigationTarget | null {
  const level = childLevel[kind];
  if (!level) return null;
  if (kind === 'finca') return model.fincas.has(id) ? { level, scope: { fincaIds: [id], suerteIds: [], tablonIds: [] } } : null;
  const suerte = model.suertes.get(id);
  return suerte ? { level, scope: { fincaIds: [suerte.fincaId], suerteIds: [id], tablonIds: [] } } : null;
}

export const drillDownLabel = (kind: EntityViewLevel): string | null => kind === 'finca' ? 'Ver Suertes' : kind === 'suerte' ? 'Ver Tablones' : null;

/**
 * Breadcrumb derived from the WorkScope (the single source of truth). A multi-entity scope is shown
 * as one aggregate crumb, so the user always sees where the current view comes from.
 */
export function breadcrumbForScope(model: LayerModel | null, requested: WorkScope, level: EntityViewLevel): readonly Crumb[] {
  const root: Crumb = { label: 'CASUR', level: 'finca', scope: emptyWorkScope(), current: false };
  if (!model) return [];
  const scope = normalizeWorkScope(model, requested);
  const crumbs: Crumb[] = [root];
  if (scope.fincaIds.length === 1) {
    const finca = model.fincas.get(scope.fincaIds[0]!)!;
    crumbs.push({ label: finca.name, level: 'suerte', scope: { fincaIds: [finca.id], suerteIds: [], tablonIds: [] }, focus: undefined, current: false });
    if (scope.suerteIds.length === 1) {
      const suerte = model.suertes.get(scope.suerteIds[0]!)!;
      crumbs.push({ label: `Suerte ${suerte.label}`, level: 'tablon', scope: { fincaIds: [finca.id], suerteIds: [suerte.id], tablonIds: [] }, current: false });
      if (scope.tablonIds.length) crumbs.push({ label: scope.tablonIds.length === 1 ? `Tablón ${model.tablones.get(scope.tablonIds[0]!)?.code ?? ''}` : `${scope.tablonIds.length} tablones`, level, scope, current: false });
    } else if (scope.suerteIds.length > 1) {
      crumbs.push({ label: `${scope.suerteIds.length} suertes`, level, scope, current: false });
    }
  } else if (scope.fincaIds.length > 1) {
    crumbs.push({ label: `${scope.fincaIds.length} fincas`, level, scope, current: false });
  }
  return crumbs.map((crumb, index) => ({ ...crumb, current: index === crumbs.length - 1 }));
}

/** One step back: Suerte scope → its Finca; Finca scope → all CASUR. The entity left behind stays selected. */
export function navigateUpTarget(model: LayerModel | null, requested: WorkScope, level: EntityViewLevel): NavigationTarget | null {
  if (!model) return null;
  const scope = normalizeWorkScope(model, requested);
  if (scope.suerteIds.length === 1 && scope.fincaIds.length === 1) return { level: 'suerte', scope: { fincaIds: scope.fincaIds, suerteIds: [], tablonIds: [] }, focus: { kind: 'suerte', id: scope.suerteIds[0]! } };
  if (scope.fincaIds.length === 1) return { level: 'finca', scope: emptyWorkScope(), focus: { kind: 'finca', id: scope.fincaIds[0]! } };
  if (scope.fincaIds.length > 1) return { level: 'finca', scope: emptyWorkScope() };
  if (level === 'tablon') return { level: 'suerte', scope };
  if (level === 'suerte') return { level: 'finca', scope };
  return null;
}
